// =====================================================================
// PLENARIUM - Eleicao da Mesa Diretora: cedula no painel do vereador
// (PLENARIUM:ELEICAO_MESA). Componente independente, por cima do painel:
//  - aparece SOZINHO quando o admin abre a votacao da eleicao;
//  - o vereador escolhe a chapa (ou abstencao), confirma, e pronto;
//  - no voto secreto a tela mostra so "Voto registrado" (o servidor nem
//    sabe em quem foi); no aberto, mostra a chapa escolhida;
//  - ao encerrar, mostra o resultado (ou o empate) e pode ser fechado.
// Conversa so com /api/eleicoes e escuta 'eleicao-atualizada'.
// Reconecta sozinho (entra de novo na sala da sessao e recarrega).
// =====================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';

const CORES = ['#2563eb', '#0d9488', '#c2410c', '#7c3aed', '#be185d', '#4d7c0f'];
const iniciais = (nome) => String(nome || '?').replace(/^Ver(\.ª|\.)\s*/i, '').split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase();

// Foto do vereador (3x4). Sem foto: iniciais.
function Foto({ url, nome, w = 46, h = 58 }) {
  return (
    <span style={{ width: w, height: h, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#e2e8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 800, fontSize: Math.round(w / 2.6) }}>
      {url ? <img src={url} alt={nome} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} /> : iniciais(nome)}
    </span>
  );
}
const z = 2147483000;

export default function EleicaoMesaVereador({ sessaoId }) {
  const [lista, setLista] = useState([]);
  const [minimizada, setMinimizada] = useState(false);
  const [escolha, setEscolha] = useState(null);       // { chapa } | { abstencao: true }
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);   // eleicao concluida/empatada vista agora
  const vistoRef = useRef({});                         // id -> ultimo status visto
  const [fotos, setFotos] = useState({});              // vereadorId -> fotoUrl

  // Fotos dos vereadores (a mesma lista que o painel ja usa).
  useEffect(() => {
    if (!sessaoId) return;
    axios.get('/api/vereadores').then(r => {
      const m = {}; (Array.isArray(r.data) ? r.data : []).forEach(v => { if (v.fotoUrl) m[v.id] = v.fotoUrl; });
      setFotos(m);
    }).catch(() => {});
  }, [sessaoId]);

  const carregar = useCallback(async () => {
    if (!sessaoId) { setLista([]); return; }
    try {
      const r = await axios.get(`/api/eleicoes/sessao/${sessaoId}`);
      const dados = r.data || [];
      // Mostra o resultado quando a eleicao muda de situacao com o painel aberto.
      dados.forEach(el => {
        const antes = vistoRef.current[el.id];
        if (antes && antes !== el.status && (el.status === 'CONCLUIDA' || el.status === 'EMPATE')) { setResultado(el.id); setMinimizada(false); }
        if (antes !== 'EM_VOTACAO' && el.status === 'EM_VOTACAO') setMinimizada(false);
        vistoRef.current[el.id] = el.status;
      });
      setLista(dados);
    } catch { /* sem eleicao ou sem rede: o painel segue normal */ }
  }, [sessaoId]);

  useEffect(() => {
    carregar();
    if (!sessaoId) return undefined;
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    let primeira = true;
    socket.on('connect', () => {
      socket.emit('entrar-sala', `sessao-${sessaoId}`);
      if (!primeira) carregar();
      primeira = false;
    });
    socket.on('eleicao-atualizada', (d) => { if (!d || d.sessaoId === sessaoId) carregar(); });
    return () => socket.disconnect();
  }, [sessaoId, carregar]);

  // Fecha o resultado sozinho depois de 20 s.
  useEffect(() => {
    if (!resultado) return undefined;
    const el = lista.find(e => e.id === resultado);
    if (!el || el.status !== 'CONCLUIDA') return undefined;
    const t = setTimeout(() => setResultado(null), 20000);
    return () => clearTimeout(t);
  }, [resultado, lista]);

  const emVotacao = lista.find(e => e.status === 'EM_VOTACAO');
  const empatada = lista.find(e => e.status === 'EMPATE');
  const elResultado = resultado ? lista.find(e => e.id === resultado) : null;
  const atual = emVotacao || (elResultado && elResultado.status === 'CONCLUIDA' ? elResultado : null) || empatada;
  if (!atual) return null;

  const confirmar = async () => {
    if (!escolha || enviando) return;
    setEnviando(true);
    try {
      await axios.post(`/api/eleicoes/${atual.id}/votar`, escolha.abstencao ? { abstencao: true } : { chapaId: escolha.chapa.id });
      toast.success('Voto registrado!');
      setEscolha(null);
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao votar. Toque em Atualizar e confira se o voto foi registrado.');
      setEscolha(null);
      await carregar();
    } finally { setEnviando(false); }
  };

  if (minimizada) {
    const txt = atual.status === 'EM_VOTACAO' ? (atual.meuVoto?.jaVotou ? '🗳️ Eleição da Mesa — voto registrado' : '🗳️ Eleição da Mesa em votação — toque para votar') : atual.status === 'EMPATE' ? '⚖️ Eleição da Mesa — empate' : '✅ Eleição da Mesa — resultado';
    return (
      <button onClick={() => setMinimizada(false)} style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 76, zIndex: z, background: atual.status === 'EM_VOTACAO' && !atual.meuVoto?.jaVotou ? '#dc2626' : '#0f172a', color: '#fff', border: 'none', borderRadius: 999, padding: '12px 22px', fontWeight: 800, fontSize: '0.95rem', boxShadow: '0 6px 24px rgba(0,0,0,.35)', cursor: 'pointer', maxWidth: '92vw' }}>
        {txt}
      </button>
    );
  }

  const jaVotou = !!atual.meuVoto?.jaVotou;
  const chapaDe = (id) => atual.chapas.find(c => c.id === id);
  const eleita = chapaDe(atual.chapaEleitaId);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: z, background: '#f1f4f9', color: '#0f172a', overflowY: 'auto', fontFamily: 'inherit' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 14px 28px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: '100%' }}>
        {/* Cabecalho */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ background: '#ede9fe', color: '#6d28d9', fontWeight: 800, fontSize: '0.8rem', padding: '5px 10px', borderRadius: 6 }}>ELEIÇÃO DA MESA DIRETORA</span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>Biênio {atual.bienio}</div>
            <div style={{ color: '#64748b', fontSize: '0.82rem' }}>
              {atual.status === 'EM_VOTACAO' ? (jaVotou ? 'Seu voto já foi registrado.' : 'Escolha uma chapa. O voto não pode ser alterado depois de confirmado.') : atual.status === 'EMPATE' ? 'Votação encerrada com empate.' : 'Votação encerrada.'}
            </div>
          </div>
          <span style={{ background: '#0f172a', color: '#fff', fontSize: '0.78rem', fontWeight: 700, padding: '6px 12px', borderRadius: 999 }}>{atual.votoSecreto ? '🔒 Voto secreto' : '🗳️ Voto aberto'}</span>
          <button onClick={() => window.location.reload()} style={botaoSec}>↻ Atualizar</button>
          <button onClick={() => { if (resultado && atual.status !== 'EM_VOTACAO') setResultado(null); else setMinimizada(true); }} style={botaoSec}>{resultado && atual.status === 'CONCLUIDA' ? 'Fechar' : 'Ver o painel'}</button>
        </div>

        {/* CEDULA */}
        {atual.status === 'EM_VOTACAO' && !jaVotou && (
          <>
            <div style={{ textAlign: 'center', color: '#475569', fontSize: '1.05rem' }}>Em qual chapa deseja votar?</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {atual.chapas.map((c, i) => {
                const cor = CORES[i % CORES.length];
                return (
                  <button key={c.id} onClick={() => setEscolha({ chapa: c })} style={{ textAlign: 'left', background: '#fff', border: `3px solid ${cor}`, borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer', position: 'relative', fontFamily: 'inherit', color: '#0f172a' }}>
                    <span style={{ position: 'absolute', top: 10, right: 14, fontSize: '2.6rem', fontWeight: 900, color: '#e2e8f0', lineHeight: 1 }}>{c.numero}</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 800, paddingRight: 44 }}>{c.nome}</span>
                    {c.membros.map(m => (
                      <span key={m.cargo} style={{ background: '#f8fafc', borderRadius: 10, padding: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Foto url={fotos[m.vereadorId]} nome={m.nome} />
                        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <small style={{ fontSize: '0.68rem', fontWeight: 800, color: cor, letterSpacing: 0.4, textTransform: 'uppercase' }}>{m.cargo}</small>
                          <b style={{ fontSize: '0.95rem' }}>{m.nome}</b>
                        </span>
                      </span>
                    ))}
                    <span style={{ marginTop: 'auto', textAlign: 'center', fontWeight: 900, fontSize: '1.05rem', padding: 12, borderRadius: 10, color: '#fff', background: cor }}>VOTAR NA CHAPA {c.numero}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setEscolha({ abstencao: true })} style={{ background: 'linear-gradient(90deg,#7c4a03,#92400e)', border: '2px solid #ca8a04', color: '#fde68a', fontWeight: 900, fontSize: '1.15rem', padding: 14, borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit' }}>ABSTENÇÃO</button>
          </>
        )}

        {/* JA VOTOU */}
        {atual.status === 'EM_VOTACAO' && jaVotou && (
          <div style={{ background: '#fff', border: '3px solid #16a34a', borderRadius: 16, padding: '30px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: '3rem' }}>✅</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#15803d' }}>
              Voto registrado{!atual.votoSecreto && atual.meuVoto ? (atual.meuVoto.abstencao ? ': Abstenção' : chapaDe(atual.meuVoto.chapaId) ? `: Chapa ${chapaDe(atual.meuVoto.chapaId).numero} · ${chapaDe(atual.meuVoto.chapaId).nome}` : '') : ''}
            </div>
            <div style={{ color: '#475569' }}>{atual.votaram} de {atual.totalVereadores} vereador(es) já votaram. Aguardando o encerramento da votação.</div>
          </div>
        )}

        {/* RESULTADO / EMPATE */}
        {(atual.status === 'CONCLUIDA' || atual.status === 'EMPATE') && (
          <>
            {atual.status === 'CONCLUIDA' && eleita && (
              <div style={{ background: '#f0fdf4', border: '3px solid #16a34a', borderRadius: 14, padding: 16, textAlign: 'center', fontSize: '1.3rem', fontWeight: 900, color: '#15803d' }}>
                ✅ ELEITA A CHAPA {eleita.numero} · {eleita.nome.toUpperCase()}
                {atual.desempatePor && <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginTop: 6 }}>Definida em caso de empate{atual.desempateFundamento ? `, conforme ${atual.desempateFundamento}` : ', conforme o Regimento Interno'}.</div>}
              </div>
            )}
            {atual.status === 'EMPATE' && (
              <div style={{ background: '#fffbeb', border: '3px solid #f59e0b', borderRadius: 14, padding: 16, textAlign: 'center', fontSize: '1.15rem', fontWeight: 900, color: '#92400e' }}>
                ⚖️ EMPATE — AGUARDANDO DEFINIÇÃO DA MESA, CONFORME O REGIMENTO INTERNO
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              {atual.chapas.map((c, i) => {
                const ganhou = c.id === atual.chapaEleitaId; const emp = atual.empatadas.includes(c.id);
                return (
                  <div key={c.id} style={{ background: ganhou ? '#f0fdf4' : '#fff', border: `3px solid ${ganhou ? '#16a34a' : emp ? '#f59e0b' : '#e2e8f0'}`, borderRadius: 14, padding: 14, opacity: atual.status === 'CONCLUIDA' && !ganhou ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <b style={{ fontSize: '1.1rem', color: CORES[i % CORES.length] }}>{c.numero} · {c.nome}</b>
                      <span style={{ fontFamily: 'monospace', fontSize: '2rem', fontWeight: 900 }}>{String(c.votos ?? 0).padStart(2, '0')}</span>
                    </div>
                    {c.membros.map(m => (
                      <div key={m.cargo} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: '0.85rem' }}>
                        <Foto url={fotos[m.vereadorId]} nome={m.nome} w={34} h={43} />
                        <span><span style={{ color: '#64748b' }}>{m.cargo}:</span> <b>{m.nome}</b></span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', color: '#475569' }}>Abstenções: <b>{atual.abstencoes ?? 0}</b> · Votaram: <b>{atual.votaram} de {atual.totalVereadores}</b></div>
          </>
        )}
      </div>

      {/* Confirmacao */}
      {escolha && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: z + 1 }}>
          <div style={{ background: '#fff', borderRadius: 18, width: 'min(560px, 100%)', padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, textAlign: 'center' }}>Confirmar seu voto?</div>
            {escolha.abstencao ? (
              <div style={{ border: '3px solid #ca8a04', borderRadius: 14, padding: 16, fontSize: '1.2rem', fontWeight: 900, color: '#92400e', textAlign: 'center' }}>ABSTENÇÃO</div>
            ) : (
              <div style={{ border: `3px solid ${CORES[atual.chapas.indexOf(escolha.chapa) % CORES.length]}`, borderRadius: 14, padding: 16 }}>
                <b style={{ fontSize: '1.2rem' }}>Chapa {escolha.chapa.numero} · {escolha.chapa.nome}</b>
                <div style={{ color: '#334155', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.6 }}>{escolha.chapa.membros.map(m => (
                  <div key={m.cargo} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                    <Foto url={fotos[m.vereadorId]} nome={m.nome} w={38} h={48} />
                    <span><span style={{ color: '#64748b' }}>{m.cargo}:</span> <b>{m.nome}</b></span>
                  </div>
                ))}</div>
              </div>
            )}
            <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center' }}>Depois de confirmado, o voto não pode ser alterado.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
              <button disabled={enviando} onClick={() => setEscolha(null)} style={{ ...botaoSec, padding: 14, fontSize: '1rem' }}>Voltar</button>
              <button disabled={enviando} onClick={confirmar} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 12, padding: 14, fontWeight: 900, fontSize: '1rem', cursor: 'pointer', opacity: enviando ? 0.6 : 1 }}>{enviando ? 'Registrando…' : 'CONFIRMAR VOTO'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const botaoSec = { background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 10, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
